import React, { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import SpecialtyEventUploadFinishMessage from '../SpecialtyEventUploadFinishMessage';
import {
  canUploadToSpecialtyEventAlbums,
  canViewSpecialtyEventAlbums,
  getSpecialtyAlbumInviteNames,
} from '../../helpers';
import { isVideoAlbum } from '../../../../../util/album';
import { fileInputAccept } from '../../../../../constants/photo-upload';
import { getCelebratedPersonName } from '../../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import Button from '../../../../../components/global/Button';
import useOpenModal from '../../../../../hooks/useOpenModal';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../constants/modals';
import ContributorShareModal from '../../../../../apps/pro/components/ContributorShare';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  authenticated: PropTypes.bool.isRequired,
  standaloneTarget: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  inputRef: PropTypes.object.isRequired,
  onUpload: PropTypes.func.isRequired,
  onAddPhotos: PropTypes.func.isRequired,
  albumUploadReset: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
};

const StandaloneStep = ({
  albumFolder,
  authenticated,
  standaloneTarget,
  inputRef,
  onUpload,
  onAddPhotos,
  albumUploadReset,
  success,
}) => {
  const history = useHistory();
  const openModal = useOpenModal();
  const celebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    false,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );
  const possessiveCelebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    true,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );
  const genericAlbumName = getSpecialtyAlbumInviteNames([standaloneTarget])[0];
  const isVideoAlbumBool = isVideoAlbum(standaloneTarget);
  const canView = canViewSpecialtyEventAlbums(
    [standaloneTarget],
    albumFolder?.event_settings,
  );
  const canUpload = canUploadToSpecialtyEventAlbums(
    [standaloneTarget],
    authenticated,
    albumFolder?.event_settings,
  );

  const hasNextSteps = !isVideoAlbumBool || canView || authenticated;

  return (
    <Fragment>
      {success ? (
        <Fragment>
          <div className="d-flex flex-column align-items-center justify-content-center">
            <h2 className="text-black mb-2 text-center">
              Thank you for your {isVideoAlbumBool ? 'videos' : 'photos'}!
            </h2>
          </div>
          <SpecialtyEventUploadFinishMessage
            uploadedTargets={[standaloneTarget]}
            possessiveCelebratedName={possessiveCelebratedName}
          />
          <hr className="w-100 mt-0 my-2" />
          {hasNextSteps && (
            <Fragment>
              <h5 className="my-1 text-center text-uppercase text-primary">
                What&apos;s next?
              </h5>
              {canView && (
                <Button
                  btnOutline
                  onClick={() =>
                    history.push(`/albums/${standaloneTarget.id}/slideshow`)
                  }
                  className="mt-1"
                  btnBlock
                >
                  View {genericAlbumName}
                </Button>
              )}
              {authenticated && (
                <Button
                  btnOutline
                  onClick={() => openModal(CONTRIBUTOR_SHARE_MODAL)}
                  className="mt-1"
                  btnBlock
                >
                  Request {isVideoAlbumBool ? 'videos' : 'photos'}
                </Button>
              )}
              {!isVideoAlbumBool && (
                <Button
                  btnOutline
                  onClick={albumUploadReset}
                  className="mt-1"
                  btnBlock
                >
                  Add more photos
                </Button>
              )}
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <h5 className="mb-1 text-center text-uppercase text-light">
            Welcome to
          </h5>
          <h2 className="text-black mb-2 text-center">
            {possessiveCelebratedName} {genericAlbumName}!
          </h2>
          {canUpload && (
            <Button onClick={onAddPhotos} className="mt-1" btnBlock>
              {isVideoAlbumBool
                ? 'Add Video Message'
                : `Add old photos of ${celebratedName}`}
            </Button>
          )}
          {canView && (
            <Button
              btnOutline
              onClick={() =>
                history.push(`/albums/${standaloneTarget.id}/slideshow`)
              }
              className="mt-1"
              btnBlock
            >
              View {genericAlbumName.toLowerCase()}
            </Button>
          )}
          <input
            id="uploader"
            type="file"
            className="d-none"
            accept={fileInputAccept}
            multiple={!isVideoAlbum(standaloneTarget)}
            onChange={event => {
              onUpload([...event.target.files], standaloneTarget);
            }}
            ref={inputRef}
          />
        </Fragment>
      )}
      <div className="mt-auto text-center">
        <div className="d-flex mt-1">
          <a href="/terms" target="_blank" className="m-auto text-xs">
            Terms of Use
          </a>
        </div>
      </div>
      <ContributorShareModal albumId={standaloneTarget.id} />
    </Fragment>
  );
};

StandaloneStep.propTypes = propTypes;

export default StandaloneStep;
